import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { REFINED } from "../risikoanalyseStep/tokens";
import { MentionTextarea } from "../notes/MentionTextarea";
import { GespraechspartnerPicker } from "./GespraechspartnerPicker";
import {
    fetchJson,
    linkQueryUrls,
    risikoRefFromRow,
    vertragRefFromRow,
} from "../vorgang/detail/linkFetch";
import {
    risikoLabel,
    vertragLabel,
    type RisikoRef,
    type VertragRef,
} from "../vorgang/detail/linkSlots";
import type {
    ContactNoteDto,
    ContactNoteInput,
    Gespraechspartner,
} from "../../services/contactNoteService";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";
import { pruefeNotizbild } from "../notizbloecke/bildDarstellung";
import { bildDateienAus } from "../notizbloecke/einfuegen";
import { kommentarHtml } from "../notizbloecke/kommentarInhalt";

/**
 * Editor für eine Kundentagebuch-Notiz (neu oder bearbeiten).
 *
 * Der Kontakt ist implizit gesetzt (wir stehen in seinen Details); zusätzlich
 * lässt sich die Notiz GENAU EINEM Vertrag ODER Risiko zuordnen — deshalb ein
 * einzelnes Auswahlfeld statt zweier Picker (das Backend weist die Kombination
 * beider ohnehin ab).
 *
 * BEWUSST KEIN Fluent-`<Dialog>`: dessen Surface liegt auf `z-index: 1000000`,
 * während sämtliche portalisierten Dropdowns dieser Codebasis (Mention-Liste,
 * Gesprächspartner-Picker, UserMultiPicker, …) auf `100000` sitzen. In einem
 * Fluent-Dialog verschwinden sie deshalb HINTER der Dialogfläche. Statt den
 * z-index hochzubieten folgt dieser Editor dem hauseigenen Overlay-Muster
 * ({@link ConfirmDialog}) und legt sich UNTER die Dropdown-Ebene — damit
 * funktionieren Erwähnungen und Partner-Suche ohne Sonderregel.
 */

/** Overlay-Ebene: unter den portalisierten Dropdowns (100000), über der Seite. */
const OVERLAY_Z_INDEX = 99_000;

interface Props {
    open: boolean;
    contactId: number;
    /** Gesetzt = Bearbeiten, sonst Anlegen. */
    editing?: ContactNoteDto | null;
    onClose: () => void;
    onSubmit: (input: ContactNoteInput) => Promise<void>;
    /** Live-Übertragung des Entwurfstextes; optional. */
    onDraftChange?: (text: string) => void;
    /**
     * Bilder in der Notiz (einfügen, hineinziehen oder „Bild hinzufügen"):
     * Upload als Notizbild-Dokument am Kontakt. Erst beim Speichern — ein
     * abgebrochener Dialog lässt kein Dokument zurück. Ohne Angabe keine Bilder.
     */
    onBildHochladen?: (file: File) => Promise<{ id: number }>;
}

/** Ein noch nicht hochgeladenes Bild mit seiner Vorschau. */
type EntwurfBild = { datei: File; url: string };

/** Wert des Zuweisungs-Selects — „" = nur Kontakt. */
type ZuweisungValue = "" | `v:${number}` | `r:${number}`;

function toZuweisung(note?: ContactNoteDto | null): ZuweisungValue {
    if (note?.contractId != null) return `v:${note.contractId}`;
    if (note?.riskObjectId != null) return `r:${note.riskObjectId}`;
    return "";
}

const labelStyle: React.CSSProperties = {
    display: "block",
    marginBottom: 5,
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: REFINED.ink2,
};

const fieldStyle: React.CSSProperties = { marginBottom: 14 };

export const NotizEditorDialog: React.FC<Props> = ({
    open,
    contactId,
    editing,
    onClose,
    onSubmit,
    onDraftChange,
    onBildHochladen,
}) => {
    const [text, setText] = useState("");
    const [bilder, setBilder] = useState<EntwurfBild[]>([]);
    const bilderRef = useRef<EntwurfBild[]>([]);
    bilderRef.current = bilder;
    const dateiRef = useRef<HTMLInputElement>(null);
    const mitBildern = !!onBildHochladen && !editing;
    const [mentions, setMentions] = useState<string[]>([]);
    const [zuweisung, setZuweisung] = useState<ZuweisungValue>("");
    const [partner, setPartner] = useState<Gespraechspartner[]>([]);
    const [vertraege, setVertraege] = useState<VertragRef[]>([]);
    const [risiken, setRisiken] = useState<RisikoRef[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Beim Öffnen aus der zu bearbeitenden Notiz (bzw. leer) initialisieren.
    useEffect(() => {
        if (!open) return;
        setText(editing?.text ?? "");
        setMentions(editing?.mentions ?? []);
        setZuweisung(toZuweisung(editing));
        setPartner(editing?.gespraechspartner ?? []);
        setError(null);
        setBilder([]);
    }, [open, editing]);

    // Vorschauen freigeben, wenn der Dialog schließt.
    useEffect(() => {
        if (open) return;
        for (const b of bilderRef.current) URL.revokeObjectURL(b.url);
    }, [open]);

    const bilderDazu = (dateien: File[]) => {
        const ok: EntwurfBild[] = [];
        for (const datei of dateien) {
            const fehler = pruefeNotizbild(datei);
            if (fehler) {
                setError(fehler);
                continue;
            }
            ok.push({ datei, url: URL.createObjectURL(datei) });
        }
        if (ok.length) setBilder((alt) => [...alt, ...ok]);
    };

    const bildWeg = (b: EntwurfBild) => {
        URL.revokeObjectURL(b.url);
        setBilder((alt) => alt.filter((x) => x !== b));
    };

    // Zuweisbare Verträge/Risiken des Kontakts (Muster + Mapper aus LinkRow).
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        void (async () => {
            try {
                const [vRows, rRows] = await Promise.all([
                    fetchJson(linkQueryUrls.contractsByContact(contactId)),
                    fetchJson(linkQueryUrls.risksByContact(contactId)),
                ]);
                if (cancelled) return;
                setVertraege(vRows.map(vertragRefFromRow).filter((v) => Number.isFinite(v.id)));
                setRisiken(rRows.map(risikoRefFromRow).filter((r) => Number.isFinite(r.id)));
            } catch {
                /* Zuweisung bleibt optional — ohne Liste schlicht „nur Kontakt" */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [open, contactId]);

    // ESC schließt (wie ConfirmDialog).
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && !saving) onClose();
        };
        alleDokumente.addEventListener("keydown", onKey);
        return () => alleDokumente.removeEventListener("keydown", onKey);
    }, [open, saving, onClose]);

    const canSave = useMemo(() => (!!text.trim() || bilder.length > 0) && !saving, [text, bilder, saving]);

    const handleSave = async () => {
        if (!canSave) return;
        setSaving(true);
        setError(null);
        try {
            const contractId = zuweisung.startsWith("v:") ? Number(zuweisung.slice(2)) : null;
            const riskObjectId = zuweisung.startsWith("r:") ? Number(zuweisung.slice(2)) : null;
            // Mit Bild wird die Notiz HTML wie im Notizfeld: Text oben, die
            // Notizbild-Marker darunter (`kommentarHtml`).
            let inhalt: Pick<ContactNoteInput, "text" | "format"> = { text: text.trim() };
            if (mitBildern && bilder.length && onBildHochladen) {
                const anhaenge = [];
                for (const b of bilder) {
                    const { id } = await onBildHochladen(b.datei);
                    anhaenge.push({ id, name: b.datei.name || "Bild", bild: true });
                }
                inhalt = { text: kommentarHtml(text, [], anhaenge), format: "html" };
            }
            await onSubmit({
                ...inhalt,
                mentions,
                contractId,
                riskObjectId,
                gespraechspartner: partner,
            });
            onClose();
        } catch (err: any) {
            setError(err?.message ?? "Notiz konnte nicht gespeichert werden");
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return createPortal(
        <div
            role="presentation"
            // Nur ein Klick auf den Backdrop SELBST schließt. Der Zielvergleich
            // ist hier wesentlich: die Dropdowns von MentionTextarea und
            // GespraechspartnerPicker sind zwar ins document.body portaliert,
            // bleiben im REACT-Baum aber Nachfahren dieses Overlays — ihre
            // Events bubbeln also hierher. Ohne den Vergleich würde die Auswahl
            // eines Vorschlags den Editor schließen.
            onMouseDown={(e) => {
                if (e.target === e.currentTarget && !saving) onClose();
            }}
            style={{
                position: "fixed",
                inset: 0,
                zIndex: OVERLAY_Z_INDEX,
                background: "rgba(20,8,20,0.34)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
                fontFamily: REFINED.fontUI,
            }}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={editing ? "Notiz bearbeiten" : "Neue Notiz"}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                    width: "min(560px, 100%)",
                    maxHeight: "calc(100dvh - 48px)",
                    overflowY: "auto",
                    background: REFINED.white,
                    border: `1px solid ${REFINED.line}`,
                    borderRadius: 14,
                    boxShadow: REFINED.cardShadow,
                    color: REFINED.ink,
                }}
            >
                <div
                    style={{
                        padding: "15px 18px",
                        borderBottom: `2px solid ${REFINED.goldLine}`,
                        fontSize: 17,
                        fontWeight: 700,
                    }}
                >
                    {editing ? "Notiz bearbeiten" : "Neue Notiz"}
                </div>

                <div style={{ padding: 18 }}>
                    <div style={fieldStyle}>
                        <label style={labelStyle} htmlFor="ktb-zuweisung">
                            Zuweisung
                        </label>
                        <select
                            id="ktb-zuweisung"
                            value={zuweisung}
                            onChange={(e) => setZuweisung(e.target.value as ZuweisungValue)}
                            style={{
                                width: "100%",
                                padding: "9px 11px",
                                border: `1px solid ${REFINED.line}`,
                                borderRadius: 10,
                                background: REFINED.fieldBg,
                                font: "inherit",
                                fontSize: 13,
                                color: REFINED.ink,
                            }}
                        >
                            <option value="">Nur dieser Kontakt</option>
                            {vertraege.length > 0 && (
                                <optgroup label="Verträge">
                                    {vertraege.map((v) => (
                                        <option key={`v${v.id}`} value={`v:${v.id}`}>
                                            {vertragLabel(v)}
                                            {v.branch ? ` · ${v.branch}` : ""}
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                            {risiken.length > 0 && (
                                <optgroup label="Risiken">
                                    {risiken.map((r) => (
                                        <option key={`r${r.id}`} value={`r:${r.id}`}>
                                            {risikoLabel(r)}
                                        </option>
                                    ))}
                                </optgroup>
                            )}
                        </select>
                    </div>

                    <div style={fieldStyle}>
                        <span style={labelStyle}>Gesprächspartner</span>
                        <GespraechspartnerPicker
                            value={partner}
                            onChange={setPartner}
                            contactId={contactId}
                        />
                    </div>

                    <div
                        style={fieldStyle}
                        {...(mitBildern
                            ? {
                                  onPasteCapture: (e: React.ClipboardEvent) => {
                                      const dateien = bildDateienAus(e.clipboardData);
                                      if (!dateien.length) return;
                                      e.preventDefault();
                                      bilderDazu(dateien);
                                  },
                                  onDragOver: (e: React.DragEvent) => {
                                      if (Array.from(e.dataTransfer?.types ?? []).includes("Files")) e.preventDefault();
                                  },
                                  onDrop: (e: React.DragEvent) => {
                                      const dateien = bildDateienAus(e.dataTransfer);
                                      if (!dateien.length) return;
                                      e.preventDefault();
                                      bilderDazu(dateien);
                                  },
                              }
                            : {})}
                    >
                        <span style={labelStyle}>Notiz</span>
                        <MentionTextarea
                            value={text}
                            onChange={({ text: t, mentions: m }) => {
                                setText(t);
                                setMentions(m);
                                onDraftChange?.(t);
                            }}
                            placeholder="Was wurde besprochen? Mit @ jemanden erwähnen"
                            minRows={5}
                            autoFocus
                        />
                        {mitBildern ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center" }}>
                                {bilder.map((b) => (
                                    <div
                                        key={b.url}
                                        style={{
                                            position: "relative",
                                            width: 72,
                                            height: 72,
                                            borderRadius: 8,
                                            border: `1px solid ${REFINED.line}`,
                                            overflow: "hidden",
                                            background: REFINED.fieldBg,
                                        }}
                                    >
                                        <img
                                            src={b.url}
                                            alt={b.datei.name}
                                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                        />
                                        <button
                                            type="button"
                                            aria-label={`${b.datei.name || "Bild"} entfernen`}
                                            title="Entfernen"
                                            disabled={saving}
                                            onClick={() => bildWeg(b)}
                                            style={{
                                                position: "absolute",
                                                top: 3,
                                                right: 3,
                                                width: 20,
                                                height: 20,
                                                padding: 0,
                                                border: "none",
                                                borderRadius: 999,
                                                background: "rgba(20,8,20,0.6)",
                                                color: REFINED.white,
                                                fontSize: 13,
                                                lineHeight: "20px",
                                                cursor: "pointer",
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    disabled={saving}
                                    onClick={() => dateiRef.current?.click()}
                                    title="Bild hinzufügen — oder mit Strg+V einfügen bzw. hineinziehen"
                                    style={{
                                        appearance: "none",
                                        font: "inherit",
                                        fontSize: REFINED.fs.sm,
                                        fontWeight: 600,
                                        padding: "6px 12px",
                                        borderRadius: 999,
                                        cursor: "pointer",
                                        background: REFINED.white,
                                        border: `1px solid ${REFINED.line}`,
                                        color: REFINED.aub,
                                    }}
                                >
                                    + Bild hinzufügen
                                </button>
                                <input
                                    ref={dateiRef}
                                    type="file"
                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                    multiple
                                    hidden
                                    onChange={(e) => {
                                        bilderDazu(Array.from(e.target.files ?? []));
                                        e.target.value = "";
                                    }}
                                />
                            </div>
                        ) : null}
                    </div>

                    {error && (
                        <div style={{ fontSize: 12, color: REFINED.err, marginTop: 8 }}>{error}</div>
                    )}

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 8,
                            marginTop: 18,
                        }}
                    >
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            style={{
                                appearance: "none",
                                font: "inherit",
                                fontSize: REFINED.fs.sm,
                                fontWeight: 600,
                                padding: "8px 16px",
                                borderRadius: 10,
                                cursor: saving ? "not-allowed" : "pointer",
                                background: REFINED.white,
                                border: `1px solid ${REFINED.line}`,
                                color: REFINED.ink,
                                opacity: saving ? 0.55 : 1,
                            }}
                        >
                            Abbrechen
                        </button>
                        <button
                            type="button"
                            onClick={handleSave}
                            disabled={!canSave}
                            style={{
                                appearance: "none",
                                font: "inherit",
                                fontSize: REFINED.fs.sm,
                                fontWeight: 700,
                                padding: "8px 16px",
                                borderRadius: 10,
                                cursor: canSave ? "pointer" : "not-allowed",
                                background: REFINED.aub,
                                border: `1px solid ${REFINED.aub}`,
                                color: REFINED.white,
                                opacity: canSave ? 1 : 0.5,
                            }}
                        >
                            {saving ? "Speichern…" : "Speichern"}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};
